<template>
  <div>
    <LoadingSpinner v-if="isLoading"/>
    <article class="form" v-else>
      <h2 class="form__title">Önskad pension <img src="../../assets/img/info.png"></h2>
      <p class="form__text">
        Här kan du testa dig fram och se hur pensionsålder och önskad pensionsnivå påverkar ditt behov av eget sparande.
      </p>
      <section class="form__section">
        <div class="form__data">
          <span>
            <p> Jag vill gå i pension vid </p>
            <label for="pensionAge"> {{pensionAge}} år </label>
          </span>
          <input
            @input="setInputStyle($event)"
            @mouseup="setTarget"
            @touchend="setTarget"
            type="range"
            min="55"
            max="75"
            v-model="pensionAge"
            ref="pensionAge"
          />
        </div>
        <p class="form__hint">
          Du kan som tidigast ta ut allmän pension vid 62 och tjänstepension vid 55.
        </p>
      </section>
      <section class="form__section">
        <div class="form__data">
          <span>
            <p> Jag önskar en pension på </p>
            <label for="monthlyPayment"> {{monthlyPayment}} kr/mån </label>
          </span>
          <input
            @input="setInputStyle($event)"
            @mouseup="setTarget"
            @touchend="setTarget"
            type="range"
            min="15000"
            max="120000"
            step="100"
            v-model="monthlyPayment"
            ref="monthlyPayment"
          >
        </div>
      </section>
      <section class="form__section">
        <div class="form__data">
          <span>
            <p> Riskprofil </p>
            <label for="riskClass">{{ riskLevelToName(Number(riskClass)) }}</label>
          </span>
          <input
            @input="setInputStyle($event)"
            @mouseup="setTarget"
            @touchend="setTarget"
            type="range"
            min="1"
            max="3"
            v-model="riskClass"
            ref="riskClass"
          >
        </div>
      </section>
      <section class="form__section">
        <div class="form__data">
          <span>
            <p> Eget nuvarande månadssparande </p>
            <label for="saving">{{ savings }} kr/mån</label>
          </span>
          <input
            @input="setInputStyle($event)"
            @mouseup="setTarget"
            @touchend="setTarget"
            type="range"
            min="0"
            max="10000"
            step="100"
            v-model="savings"
            ref="savings"
          >
        </div>
      </section>
    </article>
    <TargetResult
      :value="this.savingsMonthlyNeeded"
      :total="this.savingsTotalNeeded"
      v-model="savingsMonthlyNeeded"
    />
  </div>
</template>

<script>
import LoadingSpinner from '../elements/LoadingSpinner';
import TargetResult from '../target/TargetResult.vue'
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'TargetForm',
  components: { TargetResult, LoadingSpinner },
  data () {
    return {
      pensionAge: 0,
      monthlyPayment: 0,
      riskClass: 1,
      monthlySaving: 0,
      riskLevels: [
        { level: 1, value: 'low', name: 'Låg' },
        { level: 2, value: 'medium', name: 'Medel' },
        { level: 3, value: 'high', name: 'Hög' },
      ],
      savings: 0,
      savingsMonthlyNeeded: 0,
      savingsTotalNeeded: 0,
    }
  },
  watch: {
    savingsData: {
      deep: true,
      immediate: true,
      handler (newValue) {
        if(newValue !== null) {
          this.pensionAge = newValue.goal.pensionAge ?? this.pensionAge
          this.monthlyPayment = newValue.goal.monthlyPayment ?? this.monthlyPayment
          this.riskClass = this.riskValueToLevel(newValue.goal.riskClass ?? this.riskLevelToValue(this.riskClass))
          this.savingsMonthlyNeeded = this.numberSpacing(newValue.savingsMonthlyNeeded) ?? this.numberSpacing(this.savingsMonthlyNeeded)
          this.savings = newValue.goal.savings ?? this.savings,
          this.savingsTotalNeeded = this.numberSpacing(newValue.savingsTotalNeeded) ?? this.numberSpacing(this.savingsTotalNeeded)
        }
      }
    },
    isLoading: {
      immediate: true,
      handler (isLoading) {
        if(!isLoading) {
          this.$nextTick(this.setInputStyle)
        }
      }
    }
  },
  created () {
    if(!this.insurances.status) {
      this.getInsurances()
    }
  },
  computed: {
    ...mapGetters([ 'user', 'insurances' ]),
    isLoading: function () {
      return this.insurances.status === 'loading'
    },
    savingsData: function () {
      return this.$store.getters.insurances.calculations
    },
  },
  methods: {
    ...mapActions([ 'updateGoal', 'getInsurances']),
    numberSpacing (nr) {
      if(nr !== undefined) {
        return nr.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      } else {
        return 0
      }
    },
    riskLevelToValue (riskLevel) {
      return this.riskLevels.find(({ level }) => level === riskLevel).value
    },
    riskLevelToName (riskLevel) {
      return this.riskLevels.find(({ level }) => level === riskLevel).name
    },
    riskValueToLevel (riskValue) {
      return this.riskLevels.find(({ value }) => value === riskValue).level
    },
    checkIfNull (value) {
      if(value == null) {
        return 'X'
      } else {
        return this.numberSpacing(value)
      }
    },
    async setTarget () {
      let data = {
        monthlyPayment: this.monthlyPayment,
        riskClass: this.riskLevelToValue(Number(this.riskClass)),
        pensionAge: this.pensionAge,
        savings: this.savings
      }

      this.$store.dispatch('updateGoal', data)
    },
    setInputStyle (e = false) {
      if (e) {
        let element = e.target;
        let percent = ((element.value - element.min)/(element.max - element.min))*100
        element.style.background = `linear-gradient(90deg,rgba(112, 112, 112, .25) ${percent}% , rgba(112, 112, 112, .08) ${percent}%)`
      } else {
        Object.keys(this.$refs).map(key=>{
          let element = this.$refs[key];
          let percent = ((element.value - element.min)/(element.max - element.min))*100
          element.style.background = `linear-gradient(90deg,rgba(112, 112, 112, .25) ${percent}% , rgba(112, 112, 112, .08) ${percent}%)`
        })
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.form {
  display: flex;
  flex-direction: column;
  background-color: $light;
  border-top-left-radius: $borderRadius;
  border-top-right-radius: $borderRadius;
  padding: 2rem;

  &__title {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }

  &__text {
    margin-bottom: 30px;
  }

  &__section {
    margin: 1.25rem 0;
    flex-direction: column;
    align-items: left;
  }

  &__hint {
    margin-top: 5px;
    width: 100%;
    font-size: .75rem;
    text-align: right;
  }

  &__data {
    width: 100%;

    > span {
      display: flex;
      justify-content: space-between;
    }

    > label {
      font-family: $mediumText;
      font-size: 1.25rem;
      font-weight: bold;
      color: $gray;
    }

    > input {
      -webkit-appearance: none;
      width: 100%;
      height: 1em;
      border-radius: $rounded;
      outline: none;
      opacity: 0.8;
      transition: opacity .2s ease-out;
      box-shadow: 0 .01em .05em $gray;

      &:hover{
        opacity: 1;
      }

      &::-webkit-slider-thumb{
        -webkit-appearance: none;
        appearance: none;
        width: 1.8em;
        height: 1.8em;
        background: #0F7354 0% 0% no-repeat padding-box;
        border: .1em solid white;
        border-radius: 50%;
        cursor: pointer;
      }

      &::-moz-range-thumb{
        width: 1.8em;
        height: 1.8em;
        background: #0F7354 0% 0% no-repeat padding-box;
        border: .1em solid white;
        border-radius: 50%;
        cursor: pointer;
      }
    }
  }
}
</style>